import React, { useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaGlobe,
  FaRedoAlt,
  FaSearch,
  FaTags,
} from "react-icons/fa";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://tools.microters.com"
).replace(/\/$/, "");

const FILTERS = [
  { value: "all", label: "All tags" },
  { value: "seo", label: "SEO" },
  { value: "open-graph", label: "Open Graph" },
  { value: "twitter", label: "Twitter/X" },
  { value: "system", label: "System" },
];

const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value) => safeNumber(value).toLocaleString();

const formatBytes = (value) => {
  const bytes = safeNumber(value);
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(bytes >= 10240 ? 0 : 1)} KB`;
};

const normalizeSocial = (source) => ({
  title: String(source?.title || ""),
  description: String(source?.description || ""),
  image: String(source?.image || ""),
  imageAlt: String(source?.imageAlt || ""),
  url: String(source?.url || ""),
  type: String(source?.type || ""),
  siteName: String(source?.siteName || ""),
  locale: String(source?.locale || ""),
  card: String(source?.card || ""),
  site: String(source?.site || ""),
  creator: String(source?.creator || ""),
});

const normalizeApiResult = (payload) => {
  const data = payload && typeof payload === "object" ? payload : {};
  const metaTags = (Array.isArray(data.metaTags) ? data.metaTags : [])
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      return {
        index: safeNumber(item.index, index),
        name: String(item.name || ""),
        property: String(item.property || ""),
        httpEquiv: String(item.httpEquiv || item["http-equiv"] || ""),
        charset: String(item.charset || ""),
        content: String(item.content || ""),
      };
    })
    .filter(Boolean);

  const issues = (Array.isArray(data.issues) ? data.issues : [])
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      return {
        id: `${item.code || "ISSUE"}-${index}`,
        severity: item.severity === "error" ? "error" : "warning",
        code: String(item.code || "ISSUE"),
        field: String(item.field || "meta"),
        message: String(item.message || "Meta tag issue found."),
        fix: String(item.fix || "Review and update this meta tag."),
      };
    })
    .filter(Boolean);

  const errorCount = safeNumber(
    data.errorCount,
    issues.filter((item) => item.severity === "error").length
  );
  const warningCount = safeNumber(
    data.warningCount,
    issues.filter((item) => item.severity === "warning").length
  );
  const score = Math.max(0, Math.min(100, safeNumber(data.score)));

  return {
    requestedUrl: String(data.requestedUrl || data.url || ""),
    finalUrl: String(data.finalUrl || data.url || ""),
    hostname: String(data.hostname || ""),
    score,
    status: ["good", "review", "needs-fixes"].includes(data.status)
      ? data.status
      : score >= 85
        ? "good"
        : score >= 65
          ? "review"
          : "needs-fixes",
    title: String(data.title || ""),
    titleLength: safeNumber(data.titleLength, String(data.title || "").length),
    titleTagCount: safeNumber(data.titleTagCount),
    description: String(data.description || ""),
    descriptionLength: safeNumber(
      data.descriptionLength,
      String(data.description || "").length
    ),
    descriptionTagCount: safeNumber(data.descriptionTagCount),
    canonical: String(data.canonical || ""),
    canonicalTagCount: safeNumber(data.canonicalTagCount),
    robots: String(data.robots || ""),
    robotsEntries: Array.isArray(data.robotsEntries)
      ? data.robotsEntries
      : [],
    noindex: Boolean(data.noindex),
    nofollow: Boolean(data.nofollow),
    viewport: String(data.viewport || ""),
    charset: String(data.charset || ""),
    htmlLanguage: String(data.htmlLanguage || ""),
    keywords: String(data.keywords || ""),
    author: String(data.author || ""),
    generator: String(data.generator || ""),
    themeColor: String(data.themeColor || ""),
    applicationName: String(data.applicationName || ""),
    openGraph: normalizeSocial(data.openGraph),
    twitter: normalizeSocial(data.twitter),
    hreflang: Array.isArray(data.hreflang) ? data.hreflang : [],
    icons: Array.isArray(data.icons) ? data.icons : [],
    jsonLdCount: safeNumber(data.jsonLdCount),
    openGraphCount: safeNumber(data.openGraphCount),
    twitterCount: safeNumber(data.twitterCount),
    totalMetaTags: safeNumber(data.totalMetaTags, metaTags.length),
    metaTags,
    metaTagsTruncated: Boolean(data.metaTagsTruncated),
    issues,
    issuesTruncated: Boolean(data.issuesTruncated),
    errorCount,
    warningCount,
    passedChecks: Array.isArray(data.passedChecks)
      ? data.passedChecks.map(String).filter(Boolean)
      : [],
    httpStatus: safeNumber(data.httpStatus),
    pageBytes: safeNumber(data.pageBytes),
    redirectsFollowed: safeNumber(data.redirectsFollowed),
    checkedAt: String(data.checkedAt || ""),
  };
};

const normalizeUrlForInput = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const escapeCsv = (value) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const getTagIdentity = (tag) =>
  tag.name || tag.property || tag.httpEquiv || (tag.charset ? "charset" : "meta");

const getTagCategory = (tag) => {
  const identity = getTagIdentity(tag).toLocaleLowerCase();
  if (identity.startsWith("og:")) return "open-graph";
  if (identity.startsWith("twitter:")) return "twitter";
  if (
    [
      "description",
      "keywords",
      "robots",
      "googlebot",
      "bingbot",
      "author",
    ].includes(identity)
  ) {
    return "seo";
  }
  return "system";
};

const getScoreLabel = (status) => {
  if (status === "good") return "Good setup";
  if (status === "review") return "Review suggested";
  return "Needs fixes";
};

const MetaField = ({ label, value, count, recommended, type = "neutral" }) => (
  <article className={`mtc-meta-field ${type}`}>
    <div className="mtc-field-heading">
      <span>{label}</span>
      {typeof count === "number" ? <small>{count} characters</small> : null}
    </div>
    <p className={value ? "" : "missing"}>{value || "Not found"}</p>
    {recommended ? <small className="mtc-recommended">{recommended}</small> : null}
  </article>
);

const MetaTagsChecker = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const filteredMetaTags = useMemo(() => {
    if (!result) return [];
    const query = search.trim().toLocaleLowerCase();

    return result.metaTags.filter((tag) => {
      if (filter !== "all" && getTagCategory(tag) !== filter) return false;
      if (!query) return true;

      return [tag.name, tag.property, tag.httpEquiv, tag.charset, tag.content]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [filter, result, search]);

  const runAnalysis = async () => {
    const targetUrl = normalizeUrlForInput(url);
    if (!targetUrl) {
      setError("Please enter a target URL.");
      toast.error("Please enter a target URL.");
      return;
    }

    try {
      const parsed = new URL(targetUrl);
      if (!["http:", "https:"].includes(parsed.protocol)) throw new Error();
    } catch {
      setError("Please enter a valid HTTP or HTTPS website URL.");
      toast.error("Please enter a valid website URL.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setSearch("");
    setFilter("all");

    console.log("[META TAGS CHECKER UI] Request started.", {
      hostname: new URL(targetUrl).hostname,
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/meta-tags-checker`,
        { url: targetUrl },
        { timeout: 25000 }
      );

      if (!response.data || response.data.success === false) {
        throw new Error(
          response.data?.message || "The checker returned an invalid response."
        );
      }

      const normalized = normalizeApiResult(
        response.data.data || response.data
      );
      setResult(normalized);
      setUrl(normalized.finalUrl || targetUrl);

      console.log("[META TAGS CHECKER UI] Analysis completed.", {
        hostname: normalized.hostname,
        totalMetaTags: normalized.totalMetaTags,
        errors: normalized.errorCount,
        warnings: normalized.warningCount,
        score: normalized.score,
      });

      toast.success(
        `${formatNumber(normalized.totalMetaTags)} meta tag(s) analyzed!`,
        { theme: "colored" }
      );

      window.setTimeout(() => {
        document
          .getElementById("meta-checker-results")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (requestError) {
      const message =
        requestError.response?.data?.message ||
        (requestError.code === "ECONNABORTED"
          ? "The meta tag analysis timed out. Please try again."
          : requestError.message) ||
        "Could not analyze this website.";

      setError(message);
      toast.error(message);
      console.error("[META TAGS CHECKER UI] Request failed.", {
        status: requestError.response?.status,
        code: requestError.response?.data?.code || requestError.code,
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    runAnalysis();
  };

  const handleReset = () => {
    setUrl("");
    setLoading(false);
    setError("");
    setResult(null);
    setSearch("");
    setFilter("all");
    console.log("[META TAGS CHECKER UI] Reset completed.");
  };

  const handleCopyReport = async () => {
    if (!result) return;

    try {
      await copyText(JSON.stringify(result, null, 2));
      toast.success("Full meta report copied.");
    } catch (copyError) {
      console.error("[META TAGS CHECKER UI] Copy failed.", copyError);
      toast.error("Could not copy the report.");
    }
  };

  const handleCsvDownload = () => {
    if (!result?.metaTags?.length) return;

    const rows = [
      ["Position", "Category", "Name", "Property", "HTTP-Equiv", "Charset", "Content"],
      ...result.metaTags.map((tag) => [
        tag.index + 1,
        getTagCategory(tag),
        tag.name,
        tag.property,
        tag.httpEquiv,
        tag.charset,
        tag.content,
      ]),
    ];
    const csv = rows.map((row) => row.map(escapeCsv).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF", csv], {
      type: "text/csv;charset=utf-8",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${result.hostname || "website"}-meta-tags-report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    toast.success("CSV report downloaded.");
  };

  return (
    <main className="mtc-page">
      <style>{styles}</style>
      <ToastContainer position="top-right" autoClose={3500} />


      <section className="mtc-tool-card" aria-label="Meta tags checker">
        <form className="mtc-url-form" onSubmit={handleSubmit} noValidate>
          <div className="mtc-url-field">
            <FaGlobe aria-hidden="true" />
            <input
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value.slice(0, 2048))}
              placeholder="https://example.com/"
              disabled={loading}
              autoComplete="url"
              aria-label="Target website URL"
            />
            <button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="mtc-spinner" aria-hidden="true" /> Analyzing...
                </>
              ) : (
                <>
                  <FaSearch aria-hidden="true" /> Analyze
                </>
              )}
            </button>
          </div>
        </form>

        {error ? (
          <div className="mtc-error" role="alert">
            <FaExclamationCircle aria-hidden="true" /> {error}
          </div>
        ) : null}

        {loading ? (
          <div className="mtc-loading" aria-live="polite">
            <span className="mtc-pulse" /> Fetching the public HTML and checking meta tags...
          </div>
        ) : null}
      </section>

      {result ? (
        <section className="mtc-results" id="meta-checker-results" aria-live="polite">
          <div className="mtc-results-heading">
            <div className="mtc-page-title">
              <span>Analyzed page</span>
              <h2>{result.title || result.hostname || "Untitled page"}</h2>
              <a href={result.finalUrl} target="_blank" rel="noreferrer">
                {result.finalUrl} <FaExternalLinkAlt aria-hidden="true" />
              </a>
            </div>
            <div className="mtc-top-actions">
              <button type="button" onClick={handleCopyReport}>
                <FaCopy aria-hidden="true" /> Copy JSON
              </button>
              <button type="button" className="primary" onClick={handleCsvDownload}>
                <FaDownload aria-hidden="true" /> CSV
              </button>
              <button type="button" onClick={handleReset}>
                <FaRedoAlt aria-hidden="true" /> Reset
              </button>
            </div>
          </div>

          <div className="mtc-overview">
            <article className={`mtc-score ${result.status}`}>
              <span>Meta score</span>
              <div>
                <strong>{formatNumber(result.score)}</strong>
                <small>/100</small>
              </div>
              <p>{getScoreLabel(result.status)}</p>
              <div className="mtc-score-bar">
                <span style={{ width: `${result.score}%` }} />
              </div>
            </article>

            <div className="mtc-stat-grid">
              <article>
                <FaTags aria-hidden="true" />
                <div>
                  <strong>{formatNumber(result.totalMetaTags)}</strong>
                  <span>Meta tags</span>
                </div>
              </article>
              <article className="error">
                <FaExclamationCircle aria-hidden="true" />
                <div>
                  <strong>{formatNumber(result.errorCount)}</strong>
                  <span>Errors</span>
                </div>
              </article>
              <article className="warning">
                <FaExclamationTriangle aria-hidden="true" />
                <div>
                  <strong>{formatNumber(result.warningCount)}</strong>
                  <span>Warnings</span>
                </div>
              </article>
              <article className="passed">
                <FaCheckCircle aria-hidden="true" />
                <div>
                  <strong>{formatNumber(result.passedChecks.length)}</strong>
                  <span>Passed checks</span>
                </div>
              </article>
              <article>
                <strong>{formatNumber(result.openGraphCount)}</strong>
                <span>Open Graph</span>
              </article>
              <article>
                <strong>{formatNumber(result.twitterCount)}</strong>
                <span>Twitter/X tags</span>
              </article>
            </div>
          </div>

          <div className="mtc-page-meta">
            <span>HTTP {result.httpStatus || "—"}</span>
            <span>{formatBytes(result.pageBytes)}</span>
            <span>{formatNumber(result.redirectsFollowed)} redirect(s)</span>
            <span>{result.charset || "Charset missing"}</span>
            <span>{result.htmlLanguage || "Language missing"}</span>
            <span className={result.noindex ? "danger" : "success"}>
              {result.noindex ? "noindex" : "index allowed"}
            </span>
          </div>

          <div className="mtc-essential-grid">
            <MetaField
              label="Page title"
              value={result.title}
              count={result.titleLength}
              recommended="Common display guideline: approximately 30–60 characters."
              type={!result.title ? "error" : result.titleLength < 30 || result.titleLength > 60 ? "warning" : "good"}
            />
            <MetaField
              label="Meta description"
              value={result.description}
              count={result.descriptionLength}
              recommended="Common display guideline: approximately 70–160 characters."
              type={!result.description ? "error" : result.descriptionLength < 70 || result.descriptionLength > 160 ? "warning" : "good"}
            />
            <MetaField
              label="Canonical URL"
              value={result.canonical}
              type={result.canonical ? "good" : "warning"}
            />
            <MetaField
              label="Meta robots"
              value={result.robots || "No explicit directive; default indexing behavior applies."}
              type={result.noindex ? "warning" : "good"}
            />
          </div>

          <div className="mtc-preview-grid">
            <section className="mtc-panel">
              <div className="mtc-panel-title">
                <span className="mtc-dot" />
                <h2>Search preview</h2>
              </div>
              <div className="mtc-serp-preview">
                <span>{result.hostname}</span>
                <h3>{result.title || "Page title not found"}</h3>
                <p>{result.description || "Meta description not found."}</p>
              </div>
              <p className="mtc-preview-note">
                Search engines may rewrite or truncate displayed titles and descriptions.
              </p>
            </section>

            <section className="mtc-panel">
              <div className="mtc-panel-title">
                <span className="mtc-dot" />
                <h2>Social preview</h2>
              </div>
              <div className="mtc-social-preview">
                {result.openGraph.image ? (
                  <img
                    src={result.openGraph.image}
                    alt={result.openGraph.imageAlt || "Open Graph preview"}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="mtc-image-placeholder">No og:image</div>
                )}
                <div>
                  <span>{result.openGraph.siteName || result.hostname}</span>
                  <h3>{result.openGraph.title || result.title || "No social title"}</h3>
                  <p>{result.openGraph.description || result.description || "No social description"}</p>
                </div>
              </div>
            </section>
          </div>

          <div className="mtc-report-grid">
            <section className="mtc-panel mtc-validation-panel">
              <div className="mtc-panel-title mtc-panel-title-between">
                <div>
                  <span className="mtc-dot" />
                  <h2>Validation report</h2>
                </div>
                <small>{formatNumber(result.issues.length)} issue(s)</small>
              </div>

              {result.issues.length ? (
                <div className="mtc-issues">
                  {result.issues.map((issue) => (
                    <article key={issue.id} className={issue.severity}>
                      {issue.severity === "error" ? (
                        <FaExclamationCircle aria-hidden="true" />
                      ) : (
                        <FaExclamationTriangle aria-hidden="true" />
                      )}
                      <div>
                        <strong>{issue.message}</strong>
                        <p><b>Fix:</b> {issue.fix}</p>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="mtc-all-good">
                  <FaCheckCircle aria-hidden="true" />
                  No checked meta-tag issues were found.
                </div>
              )}

              {result.passedChecks.length ? (
                <div className="mtc-passed-checks">
                  <h3>Passed checks</h3>
                  {result.passedChecks.map((item, index) => (
                    <p key={`${item}-${index}`}>
                      <FaCheckCircle aria-hidden="true" /> {item}
                    </p>
                  ))}
                </div>
              ) : null}
            </section>

            <section className="mtc-panel mtc-social-data-panel">
              <div className="mtc-panel-title">
                <span className="mtc-dot" />
                <h2>Social and technical data</h2>
              </div>
              <div className="mtc-social-data">
                <h3>Open Graph</h3>
                {[
                  ["og:title", result.openGraph.title],
                  ["og:description", result.openGraph.description],
                  ["og:image", result.openGraph.image],
                  ["og:url", result.openGraph.url],
                  ["og:type", result.openGraph.type],
                  ["og:site_name", result.openGraph.siteName],
                ].map(([label, value]) => (
                  <p key={label}><strong>{label}</strong><span>{value || "Not found"}</span></p>
                ))}

                <h3>Twitter/X</h3>
                {[
                  ["twitter:card", result.twitter.card],
                  ["twitter:title", result.twitter.title],
                  ["twitter:description", result.twitter.description],
                  ["twitter:image", result.twitter.image],
                  ["twitter:site", result.twitter.site],
                ].map(([label, value]) => (
                  <p key={label}><strong>{label}</strong><span>{value || "Not found"}</span></p>
                ))}

                <h3>Other</h3>
                <p><strong>viewport</strong><span>{result.viewport || "Not found"}</span></p>
                <p><strong>author</strong><span>{result.author || "Not found"}</span></p>
                <p><strong>theme-color</strong><span>{result.themeColor || "Not found"}</span></p>
                <p><strong>JSON-LD blocks</strong><span>{formatNumber(result.jsonLdCount)}</span></p>
                <p><strong>hreflang links</strong><span>{formatNumber(result.hreflang.length)}</span></p>
                <p><strong>icon links</strong><span>{formatNumber(result.icons.length)}</span></p>
              </div>
            </section>
          </div>

          <section className="mtc-panel mtc-raw-panel">
            <div className="mtc-panel-title mtc-panel-title-between">
              <div>
                <span className="mtc-dot" />
                <h2>Raw meta tags</h2>
              </div>
              <small>
                Showing {formatNumber(filteredMetaTags.length)} of {formatNumber(result.metaTags.length)}
              </small>
            </div>

            <div className="mtc-filters">
              <div className="mtc-search-box">
                <FaSearch aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search names, properties or content"
                  aria-label="Search meta tags"
                />
              </div>
              <div className="mtc-filter-buttons">
                {FILTERS.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className={filter === item.value ? "active" : ""}
                    onClick={() => setFilter(item.value)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredMetaTags.length ? (
              <div className="mtc-tag-list">
                {filteredMetaTags.map((tag) => (
                  <article key={`${tag.index}-${getTagIdentity(tag)}`}>
                    <span className={`mtc-tag-category ${getTagCategory(tag)}`}>
                      {getTagCategory(tag)}
                    </span>
                    <div>
                      <strong>{getTagIdentity(tag)}</strong>
                      <p>{tag.content || tag.charset || "[Empty content]"}</p>
                    </div>
                    <small>#{tag.index + 1}</small>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mtc-no-results">No meta tags match your filters.</div>
            )}

            {result.metaTagsTruncated ? (
              <p className="mtc-limit-note">
                Display limited to the first 300 meta tags for safety.
              </p>
            ) : null}
          </section>

          <p className="mtc-disclaimer">
            The score checks selected HTML meta-tag practices. It is not a Google
            ranking score, and search engines may interpret or rewrite metadata
            differently.
          </p>
        </section>
      ) : null}
    </main>
  );
};

const styles = `
  :root {
    --mtc-bg: #fff9f7;
    --mtc-navy: #1f2d4d;
    --mtc-orange: #fa5735;
    --mtc-orange-dark: #e84527;
    --mtc-orange-soft: #fff0eb;
    --mtc-border: #dce2eb;
    --mtc-text: #4f596e;
    --mtc-muted: #8a95a9;
    --mtc-green: #138a5f;
    --mtc-green-soft: #eaf8f2;
    --mtc-yellow: #ad6800;
    --mtc-yellow-soft: #fff6df;
    --mtc-red: #d63d2e;
    --mtc-red-soft: #fdefec;
  }

  * { box-sizing: border-box; }

  .mtc-page {
    min-height: 100vh;
    padding: 82px 24px 80px;
    color: var(--mtc-navy);
    background: var(--mtc-bg);
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .mtc-hero {
    width: min(1050px, 100%);
    margin: 0 auto 66px;
    text-align: center;
  }

  .mtc-hero h1 {
    margin: 0 0 18px;
    font-size: clamp(51px, 6.1vw, 76px);
    font-weight: 850;
    line-height: 1.03;
    letter-spacing: -2.5px;
  }

  .mtc-hero h1 span { color: var(--mtc-orange); }

  .mtc-hero p {
    max-width: 930px;
    margin: 0 auto;
    color: #505767;
    font-size: 20px;
    line-height: 1.62;
  }

  .mtc-tool-card,
  .mtc-results {
    width: min(1120px, 100%);
    margin: 0 auto;
    border: 1px solid var(--mtc-border);
    border-radius: 23px;
    background: rgba(255,255,255,.78);
    box-shadow: 0 14px 34px rgba(31,45,77,.055);
  }

  .mtc-tool-card { padding: 39px; }

  .mtc-url-field {
    display: grid;
    grid-template-columns: 31px minmax(0,1fr) auto;
    min-height: 65px;
    align-items: center;
    gap: 8px;
    padding: 7px 8px 7px 14px;
    border: 1px solid #cbd4e0;
    border-radius: 5px;
    color: #8c97aa;
    background: #fff;
    box-shadow: 0 2px 4px rgba(31,45,77,.08);
    transition: border-color .2s ease, box-shadow .2s ease;
  }

  .mtc-url-field:focus-within {
    border-color: var(--mtc-orange);
    box-shadow: 0 0 0 3px rgba(250,87,53,.12);
  }

  .mtc-url-field input {
    width: 100%;
    border: 0;
    outline: 0;
    color: #3d4960;
    background: transparent;
    font: inherit;
    font-size: 18px;
  }

  .mtc-url-field button {
    display: inline-flex;
    min-width: 133px;
    min-height: 48px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 17px;
    border: 0;
    border-radius: 8px;
    color: #fff;
    background: var(--mtc-orange);
    font-size: 16px;
    font-weight: 750;
    cursor: pointer;
    transition: background .18s ease, transform .18s ease;
  }

  .mtc-url-field button:hover:not(:disabled) { transform: translateY(-1px); background: var(--mtc-orange-dark); }
  .mtc-url-field button:disabled { opacity: .72; cursor: wait; }

  .mtc-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: mtc-spin .75s linear infinite;
  }
  @keyframes mtc-spin { to { transform: rotate(360deg); } }

  .mtc-error,
  .mtc-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;
    padding: 12px 14px;
    border-radius: 9px;
    font-size: 13px;
  }
  .mtc-error { justify-content: flex-start; color: var(--mtc-red); background: var(--mtc-red-soft); }
  .mtc-loading { color: #7e899c; }

  .mtc-pulse { width: 9px; height: 9px; border-radius: 50%; background: var(--mtc-orange); animation: mtc-pulse 1s ease-in-out infinite; }
  @keyframes mtc-pulse { 50% { opacity: .3; transform: scale(.75); } }

  .mtc-results { margin-top: 30px; padding: 30px; scroll-margin-top: 24px; }

  .mtc-results-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    padding-bottom: 22px;
    border-bottom: 1px solid #e7ebf1;
  }

  .mtc-page-title > span { color: var(--mtc-orange); font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .08em; }
  .mtc-page-title h2 { max-width: 660px; margin: 6px 0; overflow: hidden; color: #172139; font-size: 26px; text-overflow: ellipsis; white-space: nowrap; }
  .mtc-page-title a { display: inline-flex; max-width: 670px; align-items: center; gap: 6px; overflow: hidden; color: #788399; font-size: 12px; text-decoration: none; text-overflow: ellipsis; white-space: nowrap; }

  .mtc-top-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 7px; }
  .mtc-top-actions button {
    display: inline-flex;
    min-height: 38px;
    align-items: center;
    gap: 6px;
    padding: 0 11px;
    border: 1px solid #ccd4df;
    border-radius: 8px;
    color: #68748a;
    background: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
  }
  .mtc-top-actions button.primary { border-color: var(--mtc-orange); color: #fff; background: var(--mtc-orange); }

  .mtc-overview { display: grid; grid-template-columns: 245px minmax(0,1fr); gap: 15px; margin-top: 21px; }

  .mtc-score { padding: 20px; border: 1px solid #e1e6ee; border-radius: 14px; background: #fff; }
  .mtc-score > span { color: #737e91; font-size: 11px; font-weight: 760; text-transform: uppercase; }
  .mtc-score > div:not(.mtc-score-bar) { margin: 8px 0 3px; }
  .mtc-score strong { font-size: 45px; line-height: 1; }
  .mtc-score small { color: #7d8799; font-size: 16px; font-weight: 700; }
  .mtc-score p { margin: 8px 0 12px; font-size: 13px; font-weight: 750; }
  .mtc-score.good strong, .mtc-score.good p { color: var(--mtc-green); }
  .mtc-score.review strong, .mtc-score.review p { color: var(--mtc-yellow); }
  .mtc-score.needs-fixes strong, .mtc-score.needs-fixes p { color: var(--mtc-red); }
  .mtc-score-bar { height: 7px; overflow: hidden; border-radius: 999px; background: #edf0f4; }
  .mtc-score-bar span { display: block; height: 100%; border-radius: inherit; background: var(--mtc-orange); }
  .mtc-score.good .mtc-score-bar span { background: var(--mtc-green); }
  .mtc-score.review .mtc-score-bar span { background: var(--mtc-yellow); }
  .mtc-score.needs-fixes .mtc-score-bar span { background: var(--mtc-red); }

  .mtc-stat-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 9px; }
  .mtc-stat-grid article { display: flex; min-width: 0; align-items: center; gap: 11px; padding: 15px; border: 1px solid #e1e6ee; border-radius: 12px; background: #fff; }
  .mtc-stat-grid article > svg { color: #788398; }
  .mtc-stat-grid strong, .mtc-stat-grid span { display: block; }
  .mtc-stat-grid strong { overflow: hidden; color: #243048; font-size: 21px; text-overflow: ellipsis; white-space: nowrap; }
  .mtc-stat-grid span { margin-top: 3px; color: #8791a4; font-size: 10px; text-transform: uppercase; }
  .mtc-stat-grid article.error svg, .mtc-stat-grid article.error strong { color: var(--mtc-red); }
  .mtc-stat-grid article.warning svg, .mtc-stat-grid article.warning strong { color: var(--mtc-yellow); }
  .mtc-stat-grid article.passed svg, .mtc-stat-grid article.passed strong { color: var(--mtc-green); }

  .mtc-page-meta { display: flex; flex-wrap: wrap; gap: 7px; margin: 15px 0; }
  .mtc-page-meta span { padding: 6px 9px; border-radius: 999px; color: #6f7a8e; background: #f1f4f7; font-size: 10px; font-weight: 700; }
  .mtc-page-meta span.danger { color: var(--mtc-red); background: var(--mtc-red-soft); }
  .mtc-page-meta span.success { color: var(--mtc-green); background: var(--mtc-green-soft); }

  .mtc-essential-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; margin-bottom: 15px; }
  .mtc-meta-field { min-width: 0; padding: 15px; border: 1px solid #e1e6ed; border-left-width: 4px; border-radius: 11px; background: #fff; }
  .mtc-meta-field.good { border-left-color: var(--mtc-green); }
  .mtc-meta-field.warning { border-left-color: var(--mtc-yellow); }
  .mtc-meta-field.error { border-left-color: var(--mtc-red); }
  .mtc-field-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .mtc-field-heading span { color: #28334a; font-size: 12px; font-weight: 780; }
  .mtc-field-heading small { color: #8e98aa; font-size: 9px; white-space: nowrap; }
  .mtc-meta-field p { margin: 9px 0 0; overflow-wrap: anywhere; color: #5f6a7e; font-size: 12px; line-height: 1.5; }
  .mtc-meta-field p.missing { color: var(--mtc-red); }
  .mtc-recommended { display: block; margin-top: 8px; color: #939daf; font-size: 9px; }

  .mtc-preview-grid, .mtc-report-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px; }
  .mtc-report-grid { grid-template-columns: minmax(0,1fr) minmax(0,.9fr); }

  .mtc-panel { min-width: 0; padding: 20px; border: 1px solid #e0e5ed; border-radius: 14px; background: #fff; }
  .mtc-panel-title, .mtc-panel-title > div { display: flex; align-items: center; gap: 8px; }
  .mtc-panel-title-between { justify-content: space-between; }
  .mtc-panel-title h2 { margin: 0; color: #1a243a; font-size: 18px; }
  .mtc-panel-title small { color: #8b95a7; font-size: 10px; }
  .mtc-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--mtc-orange); }

  .mtc-serp-preview { margin-top: 16px; padding: 17px; border: 1px solid #edf0f4; border-radius: 11px; }
  .mtc-serp-preview span { color: #28764f; font-size: 11px; }
  .mtc-serp-preview h3 { margin: 6px 0; color: #2359a8; font-size: 19px; font-weight: 500; line-height: 1.3; }
  .mtc-serp-preview p { margin: 0; color: #555f70; font-size: 12px; line-height: 1.55; }
  .mtc-preview-note { margin: 9px 0 0; color: #929bad; font-size: 9px; }

  .mtc-social-preview { margin-top: 16px; overflow: hidden; border: 1px solid #e4e8ee; border-radius: 11px; }
  .mtc-social-preview img, .mtc-image-placeholder { display: grid; width: 100%; height: 140px; place-items: center; object-fit: cover; color: #9aa3b3; background: #f2f4f7; font-size: 12px; }
  .mtc-social-preview > div:last-child { padding: 13px; }
  .mtc-social-preview span { color: #8892a4; font-size: 9px; text-transform: uppercase; }
  .mtc-social-preview h3 { margin: 5px 0; color: #273147; font-size: 15px; line-height: 1.35; }
  .mtc-social-preview p { margin: 0; color: #697488; font-size: 11px; line-height: 1.45; }

  .mtc-issues { display: grid; gap: 9px; margin-top: 15px; }
  .mtc-issues article { display: grid; grid-template-columns: 22px minmax(0,1fr); gap: 7px; padding: 12px; border-radius: 9px; }
  .mtc-issues article.error { color: var(--mtc-red); background: var(--mtc-red-soft); }
  .mtc-issues article.warning { color: var(--mtc-yellow); background: var(--mtc-yellow-soft); }
  .mtc-issues strong { display: block; font-size: 12px; line-height: 1.45; }
  .mtc-issues p { margin: 4px 0 0; color: #737d8f; font-size: 10px; line-height: 1.5; }
  .mtc-all-good { display: flex; align-items: center; gap: 9px; margin-top: 15px; padding: 14px; border-radius: 9px; color: var(--mtc-green); background: var(--mtc-green-soft); font-size: 12px; font-weight: 700; }

  .mtc-passed-checks { margin-top: 18px; }
  .mtc-passed-checks h3 { margin: 0 0 9px; font-size: 13px; }
  .mtc-passed-checks p { display: flex; align-items: flex-start; gap: 7px; margin: 7px 0; color: var(--mtc-green); font-size: 11px; line-height: 1.4; }

  .mtc-social-data { margin-top: 15px; }
  .mtc-social-data h3 { margin: 16px 0 8px; color: #28334a; font-size: 12px; }
  .mtc-social-data h3:first-child { margin-top: 0; }
  .mtc-social-data p { display: grid; grid-template-columns: 115px minmax(0,1fr); gap: 9px; margin: 0; padding: 8px 0; border-bottom: 1px solid #eef1f4; font-size: 10px; }
  .mtc-social-data p strong { color: #5c687d; }
  .mtc-social-data p span { overflow-wrap: anywhere; color: #8993a5; }

  .mtc-filters { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 16px 0; }
  .mtc-search-box { display: flex; width: min(390px,100%); height: 40px; align-items: center; gap: 8px; padding: 0 11px; border: 1px solid #d8dee7; border-radius: 8px; color: #8994a7; }
  .mtc-search-box input { width: 100%; border: 0; outline: 0; color: #3f4a61; background: transparent; font: inherit; font-size: 12px; }
  .mtc-filter-buttons { display: flex; flex-wrap: wrap; gap: 5px; }
  .mtc-filter-buttons button { min-height: 32px; padding: 0 9px; border: 1px solid #dae0e8; border-radius: 7px; color: #7d879a; background: #fff; font-size: 9px; font-weight: 750; cursor: pointer; }
  .mtc-filter-buttons button.active { border-color: var(--mtc-orange); color: var(--mtc-orange); background: var(--mtc-orange-soft); }

  .mtc-tag-list { max-height: 650px; overflow: auto; border: 1px solid #ebeff3; border-radius: 10px; }
  .mtc-tag-list article { display: grid; grid-template-columns: 86px minmax(0,1fr) 32px; gap: 10px; padding: 12px; border-bottom: 1px solid #edf0f4; }
  .mtc-tag-list article:last-child { border-bottom: 0; }
  .mtc-tag-category { align-self: start; padding: 5px 7px; border-radius: 999px; color: #687489; background: #eef2f6; font-size: 8px; font-weight: 800; text-align: center; text-transform: uppercase; }
  .mtc-tag-category.seo { color: #277452; background: #e9f7f0; }
  .mtc-tag-category.open-graph { color: #315fa0; background: #eaf1fc; }
  .mtc-tag-category.twitter { color: #236e93; background: #e7f5fb; }
  .mtc-tag-list strong { display: block; color: #283349; font-size: 12px; }
  .mtc-tag-list p { margin: 4px 0 0; overflow-wrap: anywhere; color: #737e91; font-size: 10px; line-height: 1.45; }
  .mtc-tag-list > article > small { color: #a0a8b7; font-size: 9px; text-align: right; }
  .mtc-no-results { padding: 52px 20px; color: #919bad; text-align: center; font-size: 12px; }
  .mtc-limit-note { margin: 10px 0 0; color: #9b6c24; font-size: 9px; }

  .mtc-disclaimer { margin: 18px 0 0; padding: 12px 14px; border-radius: 9px; color: #796c67; background: #fff0eb; font-size: 10px; line-height: 1.5; text-align: center; }

  @media (max-width: 900px) {
    .mtc-page { padding-top: 50px; }
    .mtc-overview { grid-template-columns: 220px minmax(0,1fr); }
    .mtc-report-grid { grid-template-columns: 1fr; }
    .mtc-filters { align-items: flex-start; flex-direction: column; }
    .mtc-search-box { width: 100%; }
  }

  @media (max-width: 700px) {
    .mtc-page { padding: 40px 15px 60px; }
    .mtc-hero { margin-bottom: 40px; }
    .mtc-hero h1 { font-size: clamp(43px,12vw,60px); letter-spacing: -1.7px; }
    .mtc-hero p { font-size: 17px; }
    .mtc-tool-card, .mtc-results { padding: 22px 17px; border-radius: 18px; }
    .mtc-url-field { grid-template-columns: 26px minmax(0,1fr); }
    .mtc-url-field button { grid-column: 1 / -1; width: 100%; }
    .mtc-results-heading { flex-direction: column; }
    .mtc-page-title h2, .mtc-page-title a { max-width: 100%; }
    .mtc-top-actions { width: 100%; justify-content: flex-start; }
    .mtc-overview { grid-template-columns: 1fr; }
    .mtc-essential-grid, .mtc-preview-grid { grid-template-columns: 1fr; }
  }

  @media (max-width: 470px) {
    .mtc-stat-grid { grid-template-columns: 1fr 1fr; }
    .mtc-filter-buttons { width: 100%; }
    .mtc-filter-buttons button { flex: 1; }
    .mtc-tag-list article { grid-template-columns: 1fr; }
    .mtc-tag-category { width: fit-content; }
    .mtc-tag-list > article > small { text-align: left; }
  }
`;

export default MetaTagsChecker;
